import React from 'react'
import { useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { Linking } from 'react-native'
import Config from 'react-native-config'
import { Storage } from '@src/utils'
import { userSelector } from '@src/selectors/app'
import { LegalPolicyConsentKind, LegalPolicyConsentState, SurveysConfigKind } from '@src/types'
import {
  AuthStackParamList,
  CommonAuthorizedStackParamList,
  CommonStackParamList,
  OnboardingStackParamsList,
} from '@src/navigation/types'
import { Analytics, CustomEventTypes } from '@src/config'
import { Feature, useActionBottomSheet, useFeatureFlag } from '@src/components'
import { policyConsentStatusesSelector } from '@src/selectors/policyConsents'
import { OnboardingFlowType } from '../constants'
import { BaseSubscriptionOptions } from './BaseSubscriptionOptions'

export const OnboardingSubscriptionOptions = () => {
  const authNavigation = useNavigation<StackNavigationProp<AuthStackParamList>>()
  const commonNavigation = useNavigation<StackNavigationProp<CommonStackParamList>>()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()
  const onboardingNavigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()
  const user = useSelector(userSelector)

  const enableInAppVideoCallsSignUp = useFeatureFlag(Feature.InAppVideoCallsSignUp)

  const dispatch = useDispatch()

  const showActionBottomSheet = useActionBottomSheet()

  const policyConsentStatuses = useSelector(policyConsentStatusesSelector)

  const handleSignOut = () => {
    dispatch({ type: 'app/logout' })
  }

  const onSignOutPress = () => {
    showActionBottomSheet({
      title: 'Sign out of your account?',
      primaryButton: {
        text: 'Sign Out',
        type: 'primary',
        onPress: handleSignOut,
      },
      secondaryButton: {
        text: 'Cancel',
        type: 'outline',
      },
    })
  }

  const onOwnSensorPress = () => {
    if (!user) {
      Storage.set(Storage.ONBOARDING_FLOW_TYPE_SELECTED_KEY, OnboardingFlowType.OwnSensor)
    }

    if (user) {
      return authorizedNavigation.navigate('OwnSensorSelection', {
        nextScreen: 'OwnSensorFreeTrialSubscriptionOffer',
      })
    }

    commonNavigation.navigate('OwnSensorSelection', { nextScreen: 'CreateAccountOptions' })
  }

  const onPurchaseSensorPress = () => {
    if (!user) {
      Storage.set(Storage.ONBOARDING_FLOW_TYPE_SELECTED_KEY, OnboardingFlowType.PurchaseStelo)
    }

    if (user) {
      const medicalDisclaimerSurveyFinished = user.surveyLinks.find(
        (link) => link.survey.kind === SurveysConfigKind.CgmMedicalDisclaimer,
      )?.finished

      const medicalDisclaimerConsentAccepted = policyConsentStatuses.some(
        (status) =>
          status.kind === LegalPolicyConsentKind.MedicalDisclaimer &&
          status.lastPolicyConsent?.state === LegalPolicyConsentState.Accepted,
      )

      if (!medicalDisclaimerSurveyFinished && !medicalDisclaimerConsentAccepted) {
        return onboardingNavigation.navigate('MedicalDisclaimerQuestionnaire', {
          questionnaire: SurveysConfigKind.CgmMedicalDisclaimer,
          allowEarlyExit: true,
          nextScreen: { screen: 'SteloSelectSubscription' },
        })
      }

      return onboardingNavigation.navigate('SteloSelectSubscription')
    }

    authNavigation.navigate('CreateAccountOptionsStelo')
  }

  const onVideoCallsPress = async () => {
    if (user && enableInAppVideoCallsSignUp) {
      onboardingNavigation.navigate('UnifiedOnboardingVideoCallsStart')
      return
    }

    try {
      Analytics.track(CustomEventTypes.GettingStartedVideoCallsPress)
      await Linking.openURL(
        `${Config.WEB_MEMBERS_BASE_URL}/video-calls?utm_source=mobile-app&utm_campaign=getting-started`,
      )
    } catch (error) {
      console.error('Error opening video calls URL:', error)
    }
  }

  return (
    <BaseSubscriptionOptions
      title="Choose Program"
      onOwnSensorPress={onOwnSensorPress}
      onPurchaseSensorPress={onPurchaseSensorPress}
      onVideoCallsPress={onVideoCallsPress}
      goBack={user ? onSignOutPress : undefined}
    />
  )
}
